import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/http";
import { WAKE } from "@/lib/wake";

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
export const voiceSupported = !!SR;
export const isSafari = typeof navigator !== "undefined" && /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent);
const SILENCE_MS = 2500;

// Sentence-aware chunking for TTS: first chunk ≤ FIRST chars (fast start), the rest ≤ MAX.
export const splitForSpeech = (text, FIRST = 180, MAX = 380) => {
  const sentences = (text || "").replace(/\s+/g, " ").trim().match(/[^.!?]+[.!?]+["']?|[^.!?]+$/g) || [];
  const out = []; let cur = "";
  for (const raw of sentences) {
    const s = raw.trim(); if (!s) continue;
    const limit = out.length === 0 ? FIRST : MAX;
    if (cur && (cur + " " + s).length > limit) { out.push(cur); cur = s; }
    else cur = cur ? cur + " " + s : s;
  }
  if (cur) out.push(cur);
  return out;
};

const chime = (up = true) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext; if (!Ctx) return;
    const ctx = new Ctx(); const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = "sine"; o.frequency.setValueAtTime(up ? 660 : 520, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(up ? 990 : 390, ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.0001, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.32); o.onended = () => ctx.close();
  } catch { /* no audio */ }
};

// Wake-phrase session ("Hey Azordi … <talk as long as you like> … 2.5s pause") + push-to-talk, and spoken replies via server TTS.
export function useVoice({ onUtterance, onWake } = {}) {
  const [listening, setListening] = useState(false);   // wake phrase armed
  const [hearing, setHearing] = useState(false);       // capturing a session
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(SR ? "" : "unsupported");
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const armedRef = useRef(false);
  const bufRef = useRef("");
  const timerRef = useRef(null);
  const cbRef = useRef({ onUtterance, onWake });
  cbRef.current = { onUtterance, onWake };

  const stop = useCallback(() => { try { recRef.current?.stop(); } catch { /* noop */ } recRef.current = null; clearTimeout(timerRef.current); armedRef.current = false; bufRef.current = ""; setListening(false); setHearing(false); setInterim(""); }, []);

  const finishSession = useCallback((pushToTalk) => {
    clearTimeout(timerRef.current);
    const cmd = bufRef.current.replace(WAKE, "").trim();
    armedRef.current = false; bufRef.current = "";
    setHearing(false); setInterim("");
    if (cmd.length > 1 || !pushToTalk) { chime(false); cbRef.current.onUtterance?.(cmd); }
    if (pushToTalk) stop();
  }, [stop]);

  const start = useCallback((pushToTalk = false) => {
    if (!SR) return;
    stop();
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-AU";
    armedRef.current = pushToTalk;
    if (pushToTalk) { setHearing(true); chime(true); }
    rec.onresult = (ev) => {
      let finalText = "", inter = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t + " "; else inter += t;
      }
      const shown = (bufRef.current + finalText + inter).trim();
      if (!armedRef.current && WAKE.test(shown)) {
        armedRef.current = true; bufRef.current = ""; setHearing(true); chime(true); cbRef.current.onWake?.();
        audioRef.current?.pause(); setSpeaking(false);
      }
      if (!armedRef.current) return;
      if (finalText) bufRef.current += finalText;
      setInterim((bufRef.current + inter).replace(WAKE, "").trim());
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => finishSession(pushToTalk), SILENCE_MS);
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") { setError("not-allowed"); stop(); }
      else if (e.error === "audio-capture") { setError("no-mic"); stop(); }
      else if (e.error === "network") { setError("network"); }
    };
    rec.onstart = () => { setError(""); setListening(true); };
    // Safari (and Chrome after silence) end the session on their own — restart shortly after so the wake word stays armed.
    rec.onend = () => { if (recRef.current === rec) { setTimeout(() => { if (recRef.current === rec) { try { rec.start(); } catch { /* restarted */ } } }, isSafari ? 400 : 150); } };
    recRef.current = rec;
    try { rec.start(); } catch (e) { setError(String(e?.message || e)); }
  }, [stop, finishSession]);

  const seqRef = useRef(0);
  // Speak several chunks back-to-back: the first starts playing while the rest are still rendering.
  const speakSequence = useCallback(async (parts) => {
    // ElevenLabs renders ~400 chars in a few seconds but a whole brief in 80s+ — so every part is cut into sentence chunks;
    // the first is short so Azordi starts talking fast, and each next chunk renders while the current one plays.
    const list = (parts || []).filter(Boolean).flatMap((p) => splitForSpeech(p));
    if (!list.length) return;
    const run = ++seqRef.current;
    audioRef.current?.pause();
    // previous/next text lets ElevenLabs stitch prosody across chunks so it stays one voice; a failed chunk is retried once, then skipped
    const fetchPart = async (i) => {
      const body = { text: list[i].slice(0, 3900), previous_text: list[i - 1] || null, next_text: list[i + 1] || null };
      for (let attempt = 0; attempt < 2; attempt++) {
        try { const { data } = await api.post("/intelligence/speak", body, { responseType: "blob" }); return URL.createObjectURL(data); }
        catch { if (attempt === 0) await new Promise((r) => setTimeout(r, 800)); }
      }
      return null;
    };
    let next = fetchPart(0);
    setSpeaking(true);
    for (let i = 0; i < list.length; i++) {
      const url = await next;
      if (seqRef.current !== run) return;
      if (i + 1 < list.length) next = fetchPart(i + 1);
      if (!url) continue;
      await new Promise((resolve) => {
        const a = new Audio(url); audioRef.current = a;
        a.onended = () => { URL.revokeObjectURL(url); resolve(); };
        a.onerror = () => resolve();
        a.onpause = () => { if (seqRef.current !== run) resolve(); else if (!a.ended) setPaused(true); };
        a.onplay = () => setPaused(false);
        a.play().catch(resolve);
      });
    }
    if (seqRef.current === run) setSpeaking(false);
  }, []);
  const speak = useCallback((text) => speakSequence(text ? [text] : []), [speakSequence]);
  const hush = useCallback(() => { seqRef.current++; audioRef.current?.pause(); setSpeaking(false); setPaused(false); }, []);
  const pause = useCallback(() => { audioRef.current?.pause(); }, []);
  const resume = useCallback(() => { audioRef.current?.play().catch(() => {}); }, []);

  useEffect(() => () => { stop(); audioRef.current?.pause(); }, [stop]);
  return { supported: voiceSupported, isSafari, error, listening, hearing, speaking, interim, startWake: () => start(false), pushToTalk: () => start(true), endSession: () => finishSession(false), stop, speak, speakSequence, hush, pause, resume, paused };
}
